import AttributeModel from '@mm/state/model/AttributeModel';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import WidgetModel, {
  AttributeWidgetConfig,
  WidgetConfig,
} from '@mm/state/model/WidgetModel';
import React, { useEffect, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import SingleAttributeContainingTextInput from '../../../editor/components/SingleAttributeContainingTextInput/SingleAttributeContainingTextInput';
import { WidgetContainerRefProp } from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import TextInput from '../../components/TextInput';
import { useAttributeHandlers } from '../../helpers/widget-helpers/use-attribute-handlers';
import { useCommonWidgetHandlers } from '../../helpers/widget-helpers/use-common-widget-handlers';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import './AttributeWidget.pcss';

type Props = {
  widget: WidgetModel;
  draggableProvided?: DraggableProvided;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

const findAttributeById = (id: number, attributes: AttributeModel[]) =>
  attributes.find(({ id: sid }) => sid === id);

const findAttributeByName = (name: string, attrs: AttributeModel[]) =>
  attrs.find(a => a.name.toUpperCase() === name.toUpperCase());

const getAttributeNameByVariableId = (
  attributes: AttributeModel[],
  id?: number,
) => {
  const { name = '' } = (id && findAttributeById(id, attributes)) || {};
  return name;
};

export const AttributeWidget = (props: Props) => {
  const {
    settableAttributes,
    updateAttribute,
    createAttributeAndUpdateWidget,
  } = useAttributeHandlers(false, false);

  const { widget, draggableProvided, platforms } = props;

  const {
    config: { value, variableId },
  } = widget;

  const attributeNameByVariableId = getAttributeNameByVariableId(
    settableAttributes,
    variableId,
  );

  const [attributeName, setAttributeName] = useState(attributeNameByVariableId);
  const [attributeValue, setAttributeValue] = useState<string | undefined>(
    value,
  );

  useEffect(() => {
    setAttributeName(attributeNameByVariableId);
  }, [attributeNameByVariableId]);

  const { update: updateWidget } = useCommonWidgetHandlers();

  const onAttributeNameChange = (value: string) => setAttributeName(value);

  const onAttributeNameBlur = () => {
    if (!attributeName) setAttributeName(attributeNameByVariableId);

    if (attributeName) {
      const match = findAttributeByName(attributeName, settableAttributes);

      if (match) updateAttribute(widget, match);
      else createAttributeAndUpdateWidget(widget, attributeName);
    }
  };

  const onAttributeValueChange = e => {
    setAttributeValue(e.target.value);
  };

  const onAttributeValueBlur = () => {
    updateWidget({
      ...widget,
      config: {
        ...widget.config,
        value: attributeValue,
      } as any,
    });
  };

  return (
    <div className="attribute-widget__container">
      <div className="attribute-widget__content">
        <div className="attribute-widget__content-column">
          <div className="attribute-widget__heading">Attribute name</div>
          <div className="attribute-widget__input-container">
            <SingleAttributeContainingTextInput
              className="attribute-widget__name-input"
              value={attributeName}
              onChange={onAttributeNameChange}
              items={settableAttributes}
              onBlur={onAttributeNameBlur}
              placeholder="Attribute name"
            />
          </div>
        </div>
        <div className="attribute-widget__content-column">
          <div className="attribute-widget__heading">Value</div>
          <TextInput
            className="attribute-widget__value-input"
            value={attributeValue}
            onChange={onAttributeValueChange}
            onBlur={onAttributeValueBlur}
            placeholder="Enter the value here"
          />
        </div>
      </div>

      <WidgetControls
        widget={widget}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    </div>
  );
};
